import { useState, useMemo, useEffect } from 'react';
import { styled } from '@mui/system';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import Paper from '@mui/material/Paper';

import Avatar from '@mui/material/Avatar';
import DoDisturbIcon from '@mui/icons-material/DoDisturb';
import Tooltip from '@mui/material/Tooltip';
import { PencilSquare, Building } from '../svg';
import { useAppDispatch, useAppSelector } from '../../utils/redux';
import EnhancedTableToolbar from './EnhancedTableToolbar';
import EnhancedTableHead from './EnhancedTableHead';
import { Order } from '../../const/types';
import { headCells } from '../../const/arrays';
import { getComparator, stableSort } from '../../utils/helper';
import PaginationTable from './PaginationTable';
import { useNavigate } from 'react-router';
import {
  buildingIdSelector,
  buildingsSelector,
} from '../../redux/selectors/building';
import { BuildingDTO } from '../../redux/types/building.dto';
import ColorType from '../Theme/ColorType';
import { useTranslation } from 'react-i18next';
import {
  isAdminSelector,
  isBuildingManagerSelector,
  isManagerSelector,
} from '../../redux/selectors/user';
import PopupConfirmSilence from '../Modal/PopupConfirmSilence';
import {
  DelayBuilding,
  SilenceBuilding,
} from '../../redux/actions/buildingAction';
import PopupConfirmDelay from '../Modal/PopupConfirmDelay';

const rowsPerPage = 5;

const BuildingTable = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const buildings = useAppSelector(buildingsSelector);
  const isAdmin = useAppSelector(isAdminSelector);
  const isManager = useAppSelector(isManagerSelector);
  const isBuildingManager = useAppSelector(isBuildingManagerSelector);
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<keyof BuildingDTO>('name');
  const [building, setBuilding] = useState<BuildingDTO>();
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [confirmSilence, setConfirmSilence] = useState(false);
  const [confirmDelay, setConfirmDelay] = useState(false);
  const [delay, setDelay] = useState(0);

  const handleOpenSilenceModal = (building?: BuildingDTO) => {
    setConfirmSilence(true);
    building && setBuilding(building);
  };
  const handleCloseSilenceModal = () => setConfirmSilence(false);

  const handleOpenDelayModal = (building?: BuildingDTO) => {
    building && setDelay(building?.leak_detection_delay || 0);
    building && setBuilding(building);
    setConfirmDelay(true);
  };
  const handleCloseDelayModal = () => setConfirmDelay(false);

  useEffect(() => {
    const pageNumbers = [];
    for (let i = 1; i <= Math.ceil(buildings.length / rowsPerPage); i += 1) {
      pageNumbers.push(i);
    }
    setTotalPages(pageNumbers.length);
  }, [buildings]);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof BuildingDTO
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleClick = (row: any) => {
    navigate(`building-details/${row.id}`, { state: { building: row } });
  };

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - buildings.length) : 0;

  // const visibleRows = useMemo(
  //   () =>
  //     stableSort(buildings, getComparator(order, orderBy)).slice(
  //       page * rowsPerPage,
  //       page * rowsPerPage + rowsPerPage
  //     ),
  //   [buildings, order, orderBy, page]
  // );
  const visibleRows = buildings.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleSilenceBuilding = () => {
    dispatch(
      SilenceBuilding({
        buildingId: building?.id,
        handleClose: handleCloseSilenceModal,
        navigate,
        silenced: building?.is_silenced,
      })
    );
  };

  const handleDelayBuilding = () => {
    dispatch(
      DelayBuilding({
        buildingId: building?.id,
        handleClose: handleCloseDelayModal,
        navigate,
        delay: delay,
      })
    );
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Paper
        sx={{
          width: '100%',
          mb: 2,
          backgroundColor: '#F8F9FB',
          border: '1px solid #D1D9E2',
          borderRadius: '10px',
          boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.05)',
        }}
      >
        <EnhancedTableToolbar
          title={t('buildings-table-title')}
          description={t('buildings-table-description')}
        />
        <TableContainer style={{ borderTop: '2px solid #D1D9E2' }}>
          <Table
            sx={{ minWidth: 750 }}
            aria-labelledby="tableTitle"
            size={'medium'}
          >
            <EnhancedTableHead
              headCells={headCells}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {visibleRows.map((row, index) => {
                const labelId = `enhanced-table-checkbox-${index}`;
                return (
                  <TableRow
                    hover
                    role="checkbox"
                    tabIndex={-1}
                    key={row.name}
                    sx={{ borderBottom: '1px solid #D1D9E2' }}
                  >
                    <TableCell
                      component="th"
                      id={labelId}
                      scope="row"
                      padding="none"
                      sx={{ paddingLeft: '20px' }}
                    >
                      <RowContainer>
                        <Avatar
                          alt="Remy Sharp"
                          sx={{
                            width: 40,
                            height: 40,
                            marginLeft: '-7px',
                            backgroundColor: ColorType.darkGrey,
                          }}
                        >
                          <Building fill={ColorType.background} />
                        </Avatar>
                        <ColumnContainer>
                          <NameText>{row.name}</NameText>
                        </ColumnContainer>
                        {row?.is_silenced && (
                          <div
                            style={{
                              alignContent: 'center',
                              marginLeft: 2,
                              marginTop: 5,
                            }}
                          >
                            <Tooltip title={t('building-silenced')}>
                              <DoDisturbIcon
                                style={{
                                  height: 19,
                                  width: 19,
                                  color: '#FF0018',
                                }}
                              />
                            </Tooltip>
                          </div>
                        )}
                        {row?.leak_detection_delay !== 0 && (
                          <div
                            style={{
                              alignContent: 'center',
                              marginLeft: 2,
                              marginTop: 5,
                            }}
                          >
                            <Tooltip
                              title={`${t('building-timer-set')} ${
                                row?.leak_detection_delay
                              }s`}
                            >
                              <AccessTimeIcon
                                style={{
                                  height: 19,
                                  width: 19,
                                  color: '#0C5399',
                                }}
                              />
                            </Tooltip>
                          </div>
                        )}
                      </RowContainer>
                      {/* {row.name} */}
                    </TableCell>
                    <TableCell align="left">
                      <Address>{row.address}</Address>
                    </TableCell>
                    {(isAdmin || isManager || isBuildingManager) && (
                      <TableCell align="right">
                        <Tooltip
                          title={t('building-timer')}
                          onClick={() => handleOpenDelayModal(row)}
                          sx={{ marginRight: 5, cursor: 'pointer' }}
                        >
                          <AccessTimeIcon
                            style={{
                              height: 19,
                              width: 19,
                              color: '#0C5399',
                            }}
                          />
                        </Tooltip>
                      </TableCell>
                    )}
                    {(isAdmin || isManager || isBuildingManager) && (
                      <TableCell align="right" sx={{ width: '133px' }}>
                        <Tooltip
                          title={
                            row?.is_silenced
                              ? t('building-unsilence')
                              : t('building-silence')
                          }
                          onClick={() => handleOpenSilenceModal(row)}
                          sx={{ marginRight: 5, cursor: 'pointer' }}
                        >
                          <DoDisturbIcon
                            style={{
                              height: 19,
                              width: 19,
                              color: '#0C5399',
                            }}
                          />
                        </Tooltip>
                      </TableCell>
                    )}
                    <TableCell align="right" sx={{ width: '20px' }}>
                      <PencilSquare
                        height={17}
                        width={17}
                        color="#0C5399"
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleClick(row)}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: 53 * emptyRows,
                  }}
                >
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <PaginationTable
          page={page}
          totalPages={totalPages}
          setPage={setPage}
        />
      </Paper>
      <PopupConfirmSilence
        open={confirmSilence}
        handleClick={handleSilenceBuilding}
        handleClose={handleCloseSilenceModal}
        message={t('building-silence-title')}
        description={
          building?.is_silenced
            ? t('building-unsilence-description')
            : t('building-silence-description')
        }
      />
      <PopupConfirmDelay
        open={confirmDelay}
        handleClick={handleDelayBuilding}
        handleClose={handleCloseDelayModal}
        message={t('building-timer-title')}
        setDelay={setDelay}
        delay={delay}
        description={t('building-timer-description')}
      />
    </Box>
  );
};

const RowContainer = styled('div')`
  display: flex;
  flex-direction: row;
`;

const ColumnContainer = styled('div')`
  margin-left: 16px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const NameText = styled('span')`
  font-family: Inter;
  font-size: 14px;
  font-weight: bold;
  color: ${ColorType.darkGrey};
`;

const Address = styled('span')`
  font-family: 'Inter';
  font-size: 14px;
  font-weight: 500;
  color: #5f6d7e;
`;

export default BuildingTable;
